import { BotIcon, SquareKanbanIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../cn";

// Design spec §8.2 Avatar / AvatarStack, as letter tiles: a person is a radius-6 tile in one of
// DTP's six tints (--avatar-1..6, picked from the name unless given), an agent the --primary
// tile with a bot glyph, a company a --secondary square. `Avatar` (round, Base UI) stays the
// photo avatar; these are the list, board and sidebar tiles.

export type AvatarTileSize = "xs" | "sm" | "md" | "lg";
export type AvatarTint = 1 | 2 | 3 | 4 | 5 | 6;

const SIZE: Record<AvatarTileSize, string> = {
  xs: "size-4 rounded-xs text-[9px] [&_svg]:size-2.5",
  sm: "size-5 rounded-sm text-[10px] [&_svg]:size-3",
  md: "size-6 rounded-sm text-2xs [&_svg]:size-3.5",
  lg: "size-8 rounded-sm text-sm [&_svg]:size-4",
};

const TINT: Record<AvatarTint, string> = {
  1: "bg-avatar-1 text-avatar-1-foreground",
  2: "bg-avatar-2 text-avatar-2-foreground",
  3: "bg-avatar-3 text-avatar-3-foreground",
  4: "bg-avatar-4 text-avatar-4-foreground",
  5: "bg-avatar-5 text-avatar-5-foreground",
  6: "bg-avatar-6 text-avatar-6-foreground",
};

/** Stable tint for a name, so the same person keeps their colour everywhere. */
export function avatarTint(name: string): AvatarTint {
  let h = 0;
  for (const ch of name) h = (h * 31 + (ch.codePointAt(0) ?? 0)) >>> 0;
  return ((h % 6) + 1) as AvatarTint;
}

function initials(name: string, max = 2): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words
    .slice(0, max)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

export type AvatarTileProps = Omit<React.ComponentProps<"span">, "children"> & {
  name: string;
  src?: string;
  kind?: "person" | "agent" | "company";
  size?: AvatarTileSize;
  tint?: AvatarTint;
  /** Letters shown (default: one on xs and for a company, two otherwise). */
  letters?: 1 | 2;
};

export function AvatarTile({
  name,
  src,
  kind = "person",
  size = "md",
  tint,
  letters,
  className,
  ...props
}: AvatarTileProps) {
  const fill =
    kind === "agent"
      ? "bg-primary text-primary-foreground"
      : kind === "company"
        ? "bg-secondary text-secondary"
        : TINT[tint ?? avatarTint(name)];
  return (
    <span
      data-slot="avatar-tile"
      data-kind={kind}
      role="img"
      aria-label={name}
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden leading-none font-semibold select-none",
        SIZE[size],
        fill,
        className,
      )}
      {...props}
    >
      {src ? (
        <img src={src} alt="" className="size-full object-cover" />
      ) : kind === "agent" ? (
        <BotIcon aria-hidden={true} />
      ) : (
        initials(name, letters ?? (size === "xs" || kind === "company" ? 1 : 2))
      )}
    </span>
  );
}

/** BOARD-ORDER: a board's colour names one of the six tints (schema `boardColor`). */
export type BoardColor = "orange" | "green" | "purple" | "amber" | "blue" | "rose";

/** The board colours in picker order, each with its tint and its name. */
export const BOARD_COLORS: readonly { color: BoardColor; tint: AvatarTint; label: string }[] = [
  { color: "orange", tint: 1, label: "Orange" },
  { color: "green", tint: 2, label: "Green" },
  { color: "purple", tint: 3, label: "Purple" },
  { color: "amber", tint: 4, label: "Amber" },
  { color: "blue", tint: 5, label: "Blue" },
  { color: "rose", tint: 6, label: "Rose" },
];

/** The tint a board shows: its chosen colour, else the one its name picks. */
export function boardTint(name: string, color?: BoardColor | null): AvatarTint {
  return BOARD_COLORS.find((c) => c.color === color)?.tint ?? avatarTint(name);
}

export type BoardMarkProps = Omit<React.ComponentProps<"span">, "children" | "color"> & {
  /** The board's name: picks its tint, so the same board keeps its colour everywhere. */
  name: string;
  /** BOARD-ORDER: the colour chosen for the board (null or absent: the tint by name). */
  color?: BoardColor | null | undefined;
  size?: AvatarTileSize;
  tint?: AvatarTint;
};

/**
 * WORK-OS: a board's coloured mark (ClickUp's coloured list icon, Asana's project tile): a
 * tile in the board's tint with a kanban glyph, beside its name in the sidebar, the overview
 * and the board header.
 */
export function BoardMark({ name, color, size = "sm", tint, className, ...props }: BoardMarkProps) {
  return (
    <span
      data-slot="board-mark"
      aria-hidden={true}
      className={cn(
        "inline-flex shrink-0 items-center justify-center select-none",
        SIZE[size],
        TINT[tint ?? boardTint(name, color)],
        className,
      )}
      {...props}
    >
      <SquareKanbanIcon />
    </span>
  );
}

export type AvatarStackProps = Omit<React.ComponentProps<"span">, "children"> & {
  /** `title` is each tile's hover text ("Dana Whitfield · Recruiting lead"); the name otherwise. */
  items: (Pick<AvatarTileProps, "name" | "src" | "kind" | "tint"> & { title?: string })[];
  size?: AvatarTileSize;
  /** Tiles shown before "+n". */
  max?: number;
};

/**
 * How a stack overlaps, per size: the next tile (with its --card ring) covers less of the one
 * before than the space beside its letters, so every initial stays whole. The small tiles show
 * one letter in a stack (two clipped at 16-20 px: "HI DI JC").
 */
const STACK: Record<AvatarTileSize, { gap: string; ring: string; letters: 1 | 2 }> = {
  xs: { gap: "-ml-[3px]", ring: "ring-[1.5px]", letters: 1 },
  sm: { gap: "-ml-1", ring: "ring-2", letters: 1 },
  md: { gap: "-ml-0.5", ring: "ring-2", letters: 2 },
  lg: { gap: "-ml-1", ring: "ring-2", letters: 2 },
};

/** "+n": as tall as the tiles, as wide as its digits. */
const MORE: Record<AvatarTileSize, string> = {
  xs: "h-4 min-w-4 rounded-xs px-0.5 text-[9px]",
  sm: "h-5 min-w-5 rounded-sm px-1 text-[10px]",
  md: "h-6 min-w-6 rounded-sm px-1 text-2xs",
  lg: "h-8 min-w-8 rounded-sm px-1.5 text-xs",
};

/** At most three tiles (fewer with `max`), each ringed in --card; the rest collapse into "+n". */
export function AvatarStack({
  items,
  size = "xs",
  max = 3,
  className,
  ...props
}: AvatarStackProps) {
  const shown = items.slice(0, Math.min(max, 3));
  const rest = items.length - shown.length;
  const stack = STACK[size];
  // Keys by name and occurrence: two tiles may share a name ("Unknown" before people load), and
  // a repeated key left stale tiles beside the new ones (wave 7: "U U A T R +1").
  const seen = new Map<string, number>();
  const keyed = shown.map((item) => {
    const n = (seen.get(item.name) ?? 0) + 1;
    seen.set(item.name, n);
    return { key: `${item.name}#${n}`, item };
  });
  return (
    <span data-slot="avatar-stack" className={cn("inline-flex items-center", className)} {...props}>
      {keyed.map(({ key, item: { title, ...item } }, i) => (
        <AvatarTile
          key={key}
          size={size}
          letters={stack.letters}
          className={cn(stack.ring, "ring-card", i > 0 && stack.gap)}
          title={title ?? item.name}
          {...item}
        />
      ))}
      {rest > 0 ? (
        <span
          role="img"
          aria-label={`${rest} more`}
          title={items
            .slice(shown.length)
            .map((i) => i.title ?? i.name)
            .join(", ")}
          className={cn(
            "inline-flex shrink-0 items-center justify-center bg-muted leading-none font-semibold text-muted-foreground tabular-nums ring-card",
            stack.ring,
            stack.gap,
            MORE[size],
          )}
        >
          +{rest}
        </span>
      ) : null}
    </span>
  );
}
